import styled from '@emotion/styled';
import type { CodeEnum } from '@zelos/codegen/apis';
import Image from 'next/image';
import Link from 'next/link';
import pcceBadge from '../comm/images/pcce-badge.png';
import pccpBadge from '../comm/images/pccp-badge.png';
import pcsqlBadge from '../comm/images/pcsql-badge.png';
import icAlgorithm from '../comm/svgs/ic-algorithm.svg';
import icConsole from '../comm/svgs/ic-console.svg';
import icSql from '../comm/svgs/ic-sql.svg';
import pcceLogo from '../comm/svgs/pcce-logo.svg';
import pccpLogo from '../comm/svgs/pccp-logo.svg';
import pcsqlLogo from '../comm/svgs/pcsql-logo.svg';
import Collapse from '@/components/comm/Collapse';
import { breakpointMdSmall } from '@/components/pages/about/comm/lib/custom-breakpoint.service';
import LevelDescription from '@/components/pages/about/main/LevelDescription';
import { buttonStyle } from '@/components/pages/about/styles/button';
import { body02, body02Medium, body03Medium, body04 } from '@/components/pages/about/styles/typography';
import { breakpointMedium } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';

type Props = {
  classification: CodeEnum,
  as?: React.ElementType,
};

const tryoutDetails = {
  PCSQL: {
    image: {
      thumbnail: pcsqlBadge,
      logo: pcsqlLogo,
      icon: icSql,
    },
    text: {
      title: 'Programmers Certified SQL',
      summary: '데이터 추출 및 가공, 집계 및 분석에 대한 능력 평가',
      recommend:
        'SQL을 현업에서 사용하고 있거나 사용할 예정이라면  누구나. 특히, 데이터 관련 업무를 한다면 필수!',
      path: '/about/pcsql',
      levelDescriptions: [
        {
          label: 'LEVEL 1',
          content:
            '기획/마케터 등 비개발 직군이 SQL을 처음 학습하고 활용하는 수준입니다.',
        },
        {
          label: 'LEVEL 2',
          content: (
            <span>
              기획/마케터 등 비개발 직군이 DB에 접근하여 데이터를 추출하고,
              간단한 집계를 처리해 데이터 분석에 응용할 수 있는 단계입니다.
              <br />
              (* 기획자/마케터 등 비개발자 자격 요건을 충족하는 단계)
            </span>
          ),
        },
        {
          label: 'LEVEL 3',
          content: (
            <span>
              DB에 접근하여 데이터를 추출 및 가공하고, 데이터와 관련 된 다양한
              요구 사항을 처리하는 쿼리를 작성할 수 있습니다.
              <br />
              (* 데이터 분석가, 비즈니스 분석가 등의 자격 요건을 충족하는 단계)
            </span>
          ),
        },
        {
          label: 'LEVEL 4',
          content:
            'SQL 언어의 다양한 문법과 함수 사용에 막힘이 없고, 복잡한 요구 사항에 대해 창의적이고 효율적인 쿼리를 작성할 수 있는 수준입니다.',
        },
      ],
    },
  },
  PCCE: {
    image: {
      thumbnail: pcceBadge,
      logo: pcceLogo,
      icon: icAlgorithm,
    },
    text: {
      title: 'Programmers Certified Coding Essential',
      summary: '프로그래밍 문법과 알고리즘에 대한 기초~실무 역량 평가',
      recommend:
        '코딩을 배우기 시작한 학습자라면 누구나. 프로그래밍 이해가 필요한 업무를 한다면 필수!',
      path: '/about/pcce',
      levelDescriptions: [
        {
          label: 'LEVEL 1',
          content:
            '프로그래밍 개념과 기초적인 문법 지식을 가지고 간단한 문제 해결 및 프로그램을 작성할 수 있습니다.',
        },
        {
          label: 'LEVEL 2',
          content:
            '프로그래밍 기본 지식을 가지고 복잡하지 않은 프로그램 설계·구현, 디버깅 문제를 해결할 수 있습니다.',
        },
        {
          label: 'LEVEL 3',
          content:
            '프로그래밍 중급 지식을 가지고 간단한 프로그램을 요구사항에 맞게 설계·구현, 디버깅, 수정 등 테스트 케이스를 설계할 수 있습니다.',
        },
        {
          label: 'LEVEL 4',
          content:
            '프로그래밍 중급 지식을 가지고 프로그램을 요구사항에 맞게 설계·구현할 수 있는 능력이 있습니다.',
        },
      ],
    },
  },
  PCCP: {
    image: {
      thumbnail: pccpBadge,
      logo: pccpLogo,
      icon: icConsole,
    },
    text: {
      title: 'Programmers Certified Coding Professional',
      summary:
        '프로그래밍 문법, 알고리즘 이해도, SW에 대한 분석∙이해∙설계∙구현 등 전문 역량 평가',
      recommend:
        '개발자로 취업을 준비하고 있거나 이미 실무에서 개발 업무를 하고 있는 모두!',
      path: '/about/pccp',
      levelDescriptions: [
        {
          label: 'LEVEL 1',
          content:
            '프로그래밍 언어로 초급 수준의 문제를 해결할 수 있습니다. 명시된 절차를 보고 제한적인 과제를 해결할 수 있습니다.',
        },
        {
          label: 'LEVEL 2',
          content:
            '프로그래밍 언어로 중급 수준의 문제를 해결할 수 있습니다. 스스로 알고리즘을 설계하여 일반적인 과제를 해결할 수 있습니다.',
        },
        {
          label: 'LEVEL 3',
          content:
            '프로그래밍 언어로 중상 수준의 문제를 정확히 해결할 수 있습니다. 적절한 알고리즘을 설계하여 다양한 과제를 해결할 수 있습니다.',
        },
        {
          label: 'LEVEL 4',
          content:
            '프로그래밍 언어로 상급 수준의 문제를 해결할 수 있습니다. 효율적인 알고리즘을 설계하여 복잡한 과제를 해결할 수 있습니다.',
        },
        {
          label: 'LEVEL 5',
          content:
            '프로그래밍 언어로 상급 수준의 문제를 최적의 방법으로 해결할 수 있습니다. 최선의 알고리즘을 설계하여 고난도 과제를 주도적으로 해결할 수 있습니다.',
        },
      ],
    },
  },
};

const CertiTryoutInfo = ({ classification, as }: Props) => {
  const {
    image: { thumbnail, logo, icon },
    text: { title, summary, recommend, levelDescriptions, path },
  } = tryoutDetails[classification];

  return (
    <Container as={as}>
      <Figure>
        <Image src={thumbnail} alt={`${classification} 썸네일`} />
      </Figure>

      <Article>
        <Content>
          <TitleLogoArea>
            <Image src={logo} alt={`${classification} 로고`} />
            <TitleText>{title}</TitleText>
          </TitleLogoArea>

          <Summary>{summary}</Summary>

          <Recommend>
            <Image src={icon} alt={`${classification} 아이콘`} />
            {recommend}
          </Recommend>

          <Collapse
            title={<CollapseTitle>{classification} 레벨 안내</CollapseTitle>}
            item={<LevelDescription descriptions={levelDescriptions} />}
          />
        </Content>

        <Footer>
          <DetailLink href={path}>자세히 알아보기</DetailLink>
        </Footer>
      </Article>
    </Container>
  );
};

export default CertiTryoutInfo;

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  gap: 64px;

  @media (max-width: ${breakpointMedium}) {
    gap: 40px;
  }

  @media (max-width: ${breakpointMdSmall}) {
    flex-direction: column;
    align-items: center;
    gap: 48px;
  }
`;

const Figure = styled.figure`
  > img {
    width: 222px;
    height: 222px;
    filter: drop-shadow(0px 20px 30px rgba(0, 0, 0, 0.20));

    @media (max-width: ${breakpointMedium}) {
      width: 180px;
      height: 180px;
    }
  }
`;

const Article = styled.article`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 32px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: 16px;
`;

const TitleLogoArea = styled.div`
  display: flex;
  align-items: flex-end;
  align-self: stretch;
  gap: 16px;

  @media (max-width: ${breakpointMdSmall}) {
    flex-direction: column;
    align-items: center;
  }
`;

const TitleText = styled.span`
  ${body04}

  color: ${colors.textSecondary};
`;

const Summary = styled.span`
  ${body02}

  color: ${colors.textPrimary};

  @media (max-width: ${breakpointMdSmall}) {
    text-align: center;
  }
`;

const Recommend = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  padding: 12px 20px;
  background-color: ${colors.black25};
  border-radius: 8px;

  color: ${colors.textBody};
  gap: 12px;
  ${body03Medium}

  @media (max-width: ${breakpointMdSmall}) {
    padding: 12px;
  }
`;

const CollapseTitle = styled.span`
${body02Medium}

color: ${colors.textPrimary};
`;

const Footer = styled.footer`
  display: flex;
  width: 100%;
  justify-content: flex-end;

  @media (max-width: ${breakpointMdSmall}) {
    justify-content: center;
  }
`;

const DetailLink = styled(Link)`
  ${buttonStyle.secondary}
  padding: 15px 22px 16px;

  ${body03Medium}
`;
